// Páginas INTERNAS do modelo "Internacional" (topo 16): /imoveis, ficha, /condominios, /empreendimento, /empresa
// e /blog no visual do modelo (Montserrat, tudo em pílula, títulos 800 com tracking negativo, páginas CLARAS no
// cinza-claro da referência com a cor mais escura do tenant nos botões/títulos). Só importa de internacional.ts
// (paleta/vars das 4 peças) — nada aqui é usado fora do gate ehModeloInternacional: null ⇒ zero diff nos demais sites.
import type { CSSProperties } from 'react';
import { isMobile } from 'react-device-detect';
import { ehModeloInternacional, hexParaRgba, paletaInternacional, varsInternacionalInterna } from './internacional';
import type { PaletaInternacional } from './internacional';

export { ehModeloInternacional, paletaInternacional } from './internacional';

/** Montserrat única (self-hosted no globals.css) e a curva das entradas do modelo. */
export const SANS_INTERNACIONAL = "'Montserrat', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

/**
 * Vars da RAIZ de uma página interna: as --in-* da paleta + Montserrat na página toda + a página CLARA (fundo =
 * cinza-claro do modelo, texto quase-preto — a referência não tem página escura; a cor5 do tenant fica para os
 * modelos que a respeitam) + tons "de página" derivados do texto (--in-sutil/--in-borda-forte) que os módulos CSS
 * das internas leem atrás de [data-modelo='internacional']. A lateral responsiva (--in-lateral: 80/24/20) mora no
 * InternacionalInterna.module.css (.interna). Fora do topo 16 ⇒ null.
 */
export const varsInternacionalPagina = (site: any): CSSProperties | null => {
    const vi = varsInternacionalInterna(site);
    if (!vi) return null;
    const p = paletaInternacional(site);
    return {
        ...vi,
        background: p.fundo,
        color: p.texto,
        '--in-sans': SANS_INTERNACIONAL,
        '--in-ease': 'cubic-bezier(0.22, 1, 0.36, 1)',
        '--in-sutil': hexParaRgba(p.texto, 0.08, 'rgba(10, 10, 10, ALPHA)'),
        '--in-sutil-forte': hexParaRgba(p.texto, 0.16, 'rgba(10, 10, 10, ALPHA)'),
        '--in-borda-forte': hexParaRgba(p.texto, 0.32, 'rgba(10, 10, 10, ALPHA)'),
    } as CSSProperties;
};

/** Cores que o TSX precisa fora do CSS (menus do react-select em portal no <body>, spinner, slider). */
export type CoresInternas = PaletaInternacional & { sutil: string; sutilForte: string; bordaForte: string; sombra: string };
export const coresInternas = (site: any): CoresInternas | null => {
    if (!ehModeloInternacional(site)) return null;
    const p = paletaInternacional(site);
    return {
        ...p,
        sutil: hexParaRgba(p.texto, 0.08, 'rgba(10, 10, 10, ALPHA)'),
        sutilForte: hexParaRgba(p.texto, 0.16, 'rgba(10, 10, 10, ALPHA)'),
        bordaForte: hexParaRgba(p.texto, 0.32, 'rgba(10, 10, 10, ALPHA)'),
        sombra: hexParaRgba(p.base, 0.16, 'rgba(0, 26, 114, ALPHA)'),
    };
};

/**
 * react-select na língua do modelo: campo PÍLULA de 44px (raio 100). No PAINEL (branco): fundo cinza-claro sem
 * borda, foco = contorno forte + fundo branco. Na BARRA (sobre o fundo da página): branco com contorno sutil.
 * Menu: branco, raio 16, sombra longa; opção escolhida = pílula no cinza-claro.
 */
export const estilosSelectInternacional = (c: CoresInternas, painel: boolean) => {
    const texto = painel ? c.tinta : c.texto;
    return {
        control: (base: any, state: any) => ({
            ...base,
            minHeight: 44,
            borderRadius: 100,
            background: painel ? (state.isFocused ? '#ffffff' : c.fundo) : '#ffffff',
            boxShadow: 'none',
            border: `1px solid ${painel ? (state.isFocused ? c.bordaForte : 'transparent') : state.isFocused ? c.bordaForte : c.borda}`,
            padding: '0 6px 0 10px',
            cursor: 'pointer',
            transition: 'border-color .2s ease, background-color .2s ease',
            ':hover': { borderColor: c.bordaForte },
        }),
        valueContainer: (base: any) => ({ ...base, padding: '2px 6px' }),
        placeholder: (base: any) => ({ ...base, color: c.cinza, fontSize: 14, fontWeight: 500 }),
        singleValue: (base: any) => ({ ...base, color: texto, fontSize: 14, fontWeight: 600 }),
        input: (base: any) => ({ ...base, color: texto }),
        container: (base: any) => ({ ...base, color: texto }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: (base: any, state: any) => ({
            ...base,
            color: c.cinza,
            transition: 'transform .3s ease, color .2s ease',
            transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
            ':hover': { color: texto },
        }),
        clearIndicator: (base: any) => ({ ...base, color: c.cinza, ':hover': { color: texto } }),
        menu: (base: any) => ({
            ...base,
            backgroundColor: '#ffffff',
            color: c.tinta,
            border: `1px solid ${c.borda}`,
            borderRadius: 16,
            overflow: 'hidden',
            boxShadow: `0 1px 3px rgba(0, 0, 0, 0.05), 0 20px 40px -12px ${c.sombra || 'rgba(0, 0, 0, 0.18)'}`,
            zIndex: 10000,
        }),
        menuList: (base: any) => ({ ...base, padding: 6 }),
        option: (base: any, state: any) => ({
            ...base,
            borderRadius: 100,
            fontSize: 14,
            fontWeight: state.isSelected ? 700 : 500,
            cursor: 'pointer',
            backgroundColor: state.isSelected ? c.fundo : state.isFocused && !isMobile ? c.fundo : 'transparent',
            color: c.tinta,
            ':active': { backgroundColor: c.borda },
        }),
        noOptionsMessage: (base: any) => ({ ...base, color: c.cinza }),
        loadingMessage: (base: any) => ({ ...base, color: c.cinza }),
    };
};

/** "12 de outubro de 2026" sem Intl (toLocaleDateString depende do ICU → hydration mismatch). */
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
export const dataPorExtenso = (valor: any): string => {
    const d = valor instanceof Date ? valor : new Date(String(valor || '').replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
};
